import { CSRF } from '../utils/CSRF'
import type { Utterance } from '../utils/transcript-processor'
import { BaseRepository } from './BaseRepository'
import type { VoiceEdit, VoiceEditRender, VoiceEditSummary } from './types'

export interface CreateVoiceEditPayload {
  title: string
  donor_video_gcs_path: string
  initialize_render: boolean
}

export interface UpdateVoiceEditRenderPayload {
  rendered_transcript?: Partial<Utterance>[]
  submit_for_rendering?: boolean
  lipsync_enabled?: boolean
}

export class EditVoiceRepository extends BaseRepository {
  getVoiceEdits = async (): Promise<VoiceEdit[]> => {
    return (await super.getHttpClientInstance().get<VoiceEdit[]>('/editvoice/voice_edits', this.getAuthHeaders())).data
  }

  getVoiceEdit = async (id: number, summary = false): Promise<VoiceEdit> => {
    const params = new URLSearchParams()
    if (summary) {
      params.append('summary', 'true')
    }

    return (await super.getHttpClientInstance().get<VoiceEdit>(`/editvoice/voice_edits/${id}?${params.toString()}`, this.getAuthHeaders())).data
  }

  getVoiceEditsSummary = async (): Promise<VoiceEditSummary[]> => {
    return (await super.getHttpClientInstance().get<VoiceEditSummary[]>('/editvoice/voice_edits/summary', this.getAuthHeaders())).data
  }

  createVoiceEdit = async (payload: CreateVoiceEditPayload): Promise<VoiceEdit> => {
    return (await super.getHttpClientInstance().post<VoiceEdit>('/editvoice/voice_edits', { voice_edit: payload }, this.getAuthHeaders())).data
  }

  createRenderForVoiceEdit = async (id: number): Promise<VoiceEditRender> => {
    const response = await super.getHttpClientInstance().post<VoiceEditRender>(`/editvoice/voice_edits/${id}/create_render`, {}, this.getAuthHeaders())
    return response.data
  }

  getRender = async (id: number): Promise<VoiceEditRender> => {
    return (await super.getHttpClientInstance().get<VoiceEditRender>(`/editvoice/renders/${id}/status`, this.getAuthHeaders())).data
  }

  updateRender = async (id: number, payload: UpdateVoiceEditRenderPayload): Promise<VoiceEditRender> => {
    const response = await super.getHttpClientInstance().put(`/editvoice/renders/${id}`, { render: payload }, this.getAuthHeaders())
    return response.data
  }

  async cloneRender(renderId: number): Promise<VoiceEditRender> {
    const response = await super.getHttpClientInstance().post<VoiceEditRender>(`/editvoice/renders/${renderId}/clone`, {}, this.getAuthHeaders())
    return response.data
  }
  async deleteRender(id: number): Promise<void> {
    await super.getHttpClientInstance().delete(`/editvoice/renders/${id}`, {
      'X-CSRF-Token': CSRF.getToken(),
    })
  }

  async deleteVoiceEdit(id: number): Promise<void> {
    await super.getHttpClientInstance().delete(`/editvoice/voice_edits/${id}`, {
      'X-CSRF-Token': CSRF.getToken(),
    })
  }

  async resetTranscript(renderId: number): Promise<VoiceEditRender> {
    return (await super.getHttpClientInstance().post<VoiceEditRender>(`/editvoice/renders/${renderId}/reset_transcript`, {}, this.getAuthHeaders())).data
  }

  async togglePublic(renderId: number): Promise<VoiceEditRender> {
    const response = await super.getHttpClientInstance().post<VoiceEditRender>(`/editvoice/renders/${renderId}/toggle_public`, {}, this.getAuthHeaders())
    return response.data
  }

  private getAuthHeaders() {
    return {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    }
  }
}
